import React from 'react';
import type { JSX } from 'react';
import { Camera } from 'lucide-react';
import { useVerortungGeraet, type VerortungGeraet } from '../../services/verortung';
import { standortVerfuegbar } from '../../services/standortSensoren';

/**
 * Schalter der Sensor-Verortung für DIESES Gerät (App-Info, nur in der App;
 * docs/verortung-kamera-vertrag.md „Abschaltbar auf drei Ebenen“).
 *
 *   • aus ⇒ die App nutzt beim Foto die Systemkamera wie bisher, keine
 *     Sensoren, kein Ortsvorschlag — egal, was im Projekt eingestellt ist.
 *   • Einblendung aus ⇒ eingebaute Kamera ohne Einblendung und ohne Sensoren.
 *   • Fotostempel (Standard aus) ⇒ Geschoss/Höhe/Zeit unten ins Foto.
 */

const Schalter: React.FC<{
    titel: string;
    text: string;
    an: boolean;
    gesperrt?: boolean;
    onUmschalten: () => void;
}> = ({ titel, text, an, gesperrt = false, onUmschalten }) => (
    <label className={`flex items-start gap-3 py-2 ${gesperrt ? 'opacity-50' : ''}`}>
        <input
            type="checkbox"
            checked={an}
            disabled={gesperrt}
            onChange={onUmschalten}
            className="mt-1 w-5 h-5 shrink-0 accent-sky-600"
        />
        <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-800">{titel}</span>
            <span className="block text-xs text-slate-500 leading-relaxed">{text}</span>
        </span>
    </label>
);

export function VerortungGeraetKarte(): JSX.Element {
    const [geraet, setzen] = useVerortungGeraet();
    const umschalten = (feld: keyof VerortungGeraet) => setzen({ [feld]: !geraet[feld] });
    const plugin = standortVerfuegbar();
    return (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-1">
                <Camera className="w-4 h-4 text-sky-600" /> Kamera mit Ort-Einblendung
            </h2>
            <p className="text-xs text-slate-500 mb-2 leading-relaxed">
                „Kamera“ öffnet beim Mangel immer die Gerätekamera. Der Knopf „App-Kamera“ darunter öffnet die eingebaute Kamera:
                Sie zeigt live „vermutlich 2. OG“ bzw. „Gerüst 4. Lage“ und schlägt den Ort vor.
                Gilt nur auf diesem Gerät; im Projekt lässt es sich zusätzlich abschalten (Pläne → Sensor-Verortung).
            </p>
            {!plugin && (
                <p className="text-xs rounded-lg px-2.5 py-2 mb-2 bg-amber-50 text-amber-900 border border-amber-200">
                    Diese App-Version kennt die Sensoren noch nicht — bitte aktualisieren (ab 1.0.2). Bis dahin: Systemkamera.
                </p>
            )}
            <div className="divide-y divide-slate-100">
                <Schalter
                    titel="Sensor-Verortung auf diesem Gerät"
                    text="Aus: nur die Gerätekamera, kein Knopf „App-Kamera“, keine Sensoren, kein Ortsvorschlag."
                    an={geraet.aktiv}
                    onUmschalten={() => umschalten('aktiv')}
                />
                <Schalter
                    titel="Einblendung in der Kamera"
                    text="Aus: eingebaute Kamera ohne Geschoss-Anzeige und ohne Sensoren. Auch direkt in der Kamera umschaltbar."
                    an={geraet.einblendung}
                    gesperrt={!geraet.aktiv}
                    onUmschalten={() => umschalten('einblendung')}
                />
                <Schalter
                    titel="Ort ins Foto schreiben (Fotostempel)"
                    text="Schreibt z. B. „2. OG · +6,10 m · 27.09.2026 10:14“ klein unten ins Bild. Standard: aus."
                    an={geraet.fotostempel}
                    gesperrt={!geraet.aktiv}
                    onUmschalten={() => umschalten('fotostempel')}
                />
            </div>
        </section>
    );
}
